// Shared filter/compare controls for the reporting dashboards (V1.2).
// Period presets resolve client-side to {start,end} ISO dates; the backend
// takes explicit start/end, so compare mode = two fetches diffed here.

import React, { useEffect, useRef, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { ArrowDownRight, ArrowUpRight, Check, Minus, SlidersHorizontal, X } from "lucide-react";
import { cn } from "../../../lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import { Button } from "../../../ui/button";

// White cards with a clear border + soft shadow. Combined with the gray
// page backdrop (DashboardSurface) this gives the premium "raised panel"
// look from the mockups. Standard Tailwind utilities only (no arbitrary
// values) so it renders regardless of JIT config.
export const CARD_CLASS = "border border-slate-200 bg-white shadow-sm";

// ── Demographic groups shown in the popover (single-select across all) ──────
export const DEMO_GROUPS = [
  {
    label: "Profile",
    dims: [
      { key: "gender", label: "Gender" },
      { key: "age_band", label: "Age" },
      { key: "us_state", label: "State" },
      { key: "relationship_status", label: "Relationship" },
      { key: "student", label: "Student" },
    ],
  },
  {
    label: "Balance (steady vs needs attention)",
    dims: [
      { key: "balance_mind", label: "Mind" },
      { key: "balance_body", label: "Body" },
      { key: "balance_relationships", label: "Relationships" },
      { key: "balance_responsibilities", label: "Responsibilities" },
      { key: "balance_finances", label: "Finances" },
    ],
  },
];

const ALL_DEMO_DIMS = DEMO_GROUPS.flatMap((g) => g.dims);
export const demoLabel = (key) =>
  ALL_DEMO_DIMS.find((d) => d.key === key)?.label || key;

// ── Period presets (resolve relative to today, local) ───────────────────────
const iso = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};
const nearestSunday = (d) => {
  const x = startOfDay(d);
  x.setDate(x.getDate() - x.getDay());
  return x;
};

export const PERIOD_PRESETS = [
  ["this_week", "This week"],
  ["last_week", "Last week"],
  ["this_month", "This month"],
  ["last_month", "Last month"],
  ["this_quarter", "This quarter"],
  ["last_quarter", "Last quarter"],
  ["this_year", "This year (YTD)"],
  ["last_year", "Last year"],
];

export function periodRange(key) {
  const now = startOfDay(new Date());
  const y = now.getFullYear();
  const m = now.getMonth();
  const q = Math.floor(m / 3);
  switch (key) {
    case "this_week":
      return { start: iso(nearestSunday(now)), end: iso(now), label: "This week" };
    case "last_week": {
      const s = nearestSunday(now);
      const ls = new Date(s);
      ls.setDate(ls.getDate() - 7);
      const le = new Date(s);
      le.setDate(le.getDate() - 1);
      return { start: iso(ls), end: iso(le), label: "Last week" };
    }
    case "this_month":
      return { start: iso(new Date(y, m, 1)), end: iso(now), label: "This month" };
    case "last_month":
      return {
        start: iso(new Date(y, m - 1, 1)),
        end: iso(new Date(y, m, 0)),
        label: "Last month",
      };
    case "this_quarter":
      return { start: iso(new Date(y, q * 3, 1)), end: iso(now), label: "This quarter" };
    case "last_quarter": {
      let lq = q - 1;
      let ly = y;
      if (lq < 0) {
        lq = 3;
        ly = y - 1;
      }
      return {
        start: iso(new Date(ly, lq * 3, 1)),
        end: iso(new Date(ly, lq * 3 + 3, 0)),
        label: "Last quarter",
      };
    }
    case "this_year":
      return { start: iso(new Date(y, 0, 1)), end: iso(now), label: "This year (YTD)" };
    case "last_year":
      return {
        start: iso(new Date(y - 1, 0, 1)),
        end: iso(new Date(y - 1, 11, 31)),
        label: "Last year",
      };
    default:
      return { start: iso(new Date(y, m, 1)), end: iso(now), label: "This month" };
  }
}

// ── Delta helpers (compare mode) ────────────────────────────────────────────
export function deltaPct(a, b) {
  if (a == null || b == null || b === 0) return { txt: "—", dir: "flat" };
  const d = ((a - b) / Math.abs(b)) * 100;
  const dir = d > 0.5 ? "up" : d < -0.5 ? "down" : "flat";
  return { txt: `${d > 0 ? "+" : ""}${d.toFixed(0)}%`, dir };
}

export const DeltaBadge = ({ a, b, goodWhenDown = false }) => {
  const { txt, dir } = deltaPct(a, b);
  const Icon = dir === "up" ? ArrowUpRight : dir === "down" ? ArrowDownRight : Minus;
  const good = (dir === "up" && !goodWhenDown) || (dir === "down" && goodWhenDown);
  return (
    <span
      className={cn(
        "inline-flex items-center gap-0.5 text-xs font-medium tabular-nums",
        dir === "flat"
          ? "text-muted-foreground"
          : good
          ? "text-emerald-600"
          : "text-red-500"
      )}
    >
      <Icon className="h-3.5 w-3.5" />
      {txt}
    </span>
  );
};

// ── Click-outside hook ──────────────────────────────────────────────────────
function useClickOutside(onClose) {
  const ref = useRef(null);
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);
  return ref;
}

// ── Demographics dialog (MULTI-select across dims) ──────────────────────────
// Centered modal (flex-overlay pattern — immune to the ancestor-transform bug
// that pushed the translate-centered shadcn dialog to the corner). A card per
// dimension group; pick any number of values (OR within a dimension, AND
// across). `value` is a predicate {dim: [values]} | null.
const predicateCount = (pred) =>
  pred ? Object.values(pred).reduce((n, vals) => n + vals.length, 0) : 0;

export const DemographicsPopover = ({ options, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value || {});
  const count = predicateCount(value);

  useEffect(() => {
    if (open) setDraft(value || {});
  }, [open, value]);

  const groups = DEMO_GROUPS.map((g) => ({
    ...g,
    dims: g.dims.filter((d) => (options?.[d.key] || []).length > 0),
  })).filter((g) => g.dims.length > 0);

  const toggle = (dimKey, opt) =>
    setDraft((d) => {
      const current = d[dimKey] || [];
      const next = current.includes(opt)
        ? current.filter((v) => v !== opt)
        : [...current, opt];
      const out = { ...d };
      if (next.length) out[dimKey] = next;
      else delete out[dimKey];
      return out;
    });

  const apply = () => {
    onChange(Object.keys(draft).length ? draft : null);
    setOpen(false);
  };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
      <DialogPrimitive.Trigger asChild>
        <button
          type="button"
          className={cn(
            "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors",
            count
              ? "border-sky-500 bg-sky-50 text-sky-700"
              : "border-border bg-background text-muted-foreground hover:text-foreground"
          )}
        >
          <SlidersHorizontal className="h-3.5 w-3.5" />
          Demographics
          {count ? (
            <span className="ml-0.5 rounded-full bg-sky-600 px-1.5 text-[10px] font-semibold text-white">
              {count}
            </span>
          ) : null}
        </button>
      </DialogPrimitive.Trigger>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-[1px] data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        {/* Flex wrapper centers the panel in the viewport regardless of any
            transformed ancestor. Click on the wrapper (not the card) closes. */}
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) setOpen(false);
          }}
        >
          <DialogPrimitive.Content
            className="relative flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl border bg-background shadow-2xl data-[state=open]:animate-in data-[state=open]:zoom-in-95"
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            <div className="border-b px-5 py-4">
              <DialogPrimitive.Title className="text-base font-semibold">
                Filter by demographic
              </DialogPrimitive.Title>
              <DialogPrimitive.Description className="mt-0.5 text-xs text-muted-foreground">
                Pick any combination — multiple values within a group match any
                of them; across groups all must match.
              </DialogPrimitive.Description>
              <DialogPrimitive.Close className="absolute right-4 top-4 rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground">
                <X className="h-4 w-4" />
              </DialogPrimitive.Close>
            </div>

            <div className="flex-1 overflow-y-auto p-5">
              {groups.length === 0 ? (
                <p className="py-6 text-center text-sm text-muted-foreground">
                  No demographic data available yet.
                </p>
              ) : (
                <div className="grid gap-3 sm:grid-cols-2">
                  {groups.map((g) => (
                    <Card key={g.label} className="border-slate-200">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                          {g.label}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {g.dims.map((dim) => (
                          <div key={dim.key}>
                            <div className="mb-1 text-[11px] font-medium text-foreground/70">
                              {dim.label}
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                              {options[dim.key].map((opt) => {
                                const on = (draft[dim.key] || []).includes(opt);
                                return (
                                  <button
                                    key={opt}
                                    type="button"
                                    onClick={() => toggle(dim.key, opt)}
                                    className={cn(
                                      "inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors",
                                      on
                                        ? "border-sky-500 bg-sky-50 text-sky-700"
                                        : "border-border bg-background text-muted-foreground hover:border-sky-300 hover:text-foreground"
                                    )}
                                  >
                                    {on ? <Check className="h-3 w-3" /> : null}
                                    {opt}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between border-t px-5 py-3">
              <button
                type="button"
                onClick={() => setDraft({})}
                className="text-xs text-muted-foreground hover:text-foreground"
              >
                Clear all
              </button>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <Button size="sm" onClick={apply}>
                  Apply{predicateCount(draft) ? ` (${predicateCount(draft)})` : ""}
                </Button>
              </div>
            </div>
          </DialogPrimitive.Content>
        </div>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};

// ── Custom date-range popover ───────────────────────────────────────────────
export const CustomRangePopover = ({ active, onApply, onClear }) => {
  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [note, setNote] = useState("");
  const ref = useClickOutside(() => setOpen(false));

  const apply = () => {
    if (!from || !to) {
      setNote("pick both dates");
      return;
    }
    if (from > to) {
      setNote("from is after to");
      return;
    }
    setNote("");
    setOpen(false);
    onApply({ start: from, end: to });
  };

  return (
    <div className="relative inline-block" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={cn(
          "rounded-md border px-3 py-1 text-xs font-medium",
          active
            ? "border-sky-500 bg-sky-50 text-sky-700"
            : "border-border bg-background text-muted-foreground hover:text-foreground"
        )}
      >
        {active ? `${active.start} → ${active.end}` : "Custom ▾"}
      </button>
      {open ? (
        <div className="absolute left-0 z-40 mt-2 w-64 rounded-xl border border-slate-200 bg-white p-4 shadow-2xl ring-1 ring-slate-900/5">
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Custom range
          </div>
          <label className="mb-2 flex items-center justify-between gap-2 text-xs font-medium text-foreground/70">
            From
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="rounded-md border border-slate-300 px-2 py-1 text-xs shadow-sm focus:border-sky-400 focus:outline-none focus:ring-1 focus:ring-sky-300"
            />
          </label>
          <label className="mb-3 flex items-center justify-between gap-2 text-xs font-medium text-foreground/70">
            To
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="rounded-md border border-slate-300 px-2 py-1 text-xs shadow-sm focus:border-sky-400 focus:outline-none focus:ring-1 focus:ring-sky-300"
            />
          </label>
          <div className="flex items-center justify-between border-t border-slate-200 pt-3">
            <span className="text-[10px] text-red-500">{note}</span>
            <div className="flex gap-2">
              {active ? (
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    onClear();
                  }}
                  className="rounded-md px-2 py-1 text-[11px] font-medium text-muted-foreground hover:bg-slate-100"
                >
                  Clear
                </button>
              ) : null}
              <button
                type="button"
                onClick={apply}
                className="rounded-md bg-sky-600 px-3 py-1 text-[11px] font-semibold text-white hover:bg-sky-700"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

// ── Compare-period bar (A vs B preset selectors) ────────────────────────────
export const ComparePeriodBar = ({ a, b, onChange, onClose }) => (
  <div className="flex flex-wrap items-center gap-3 rounded-lg border border-amber-300/60 bg-amber-50 px-4 py-2.5">
    <div className="flex items-center gap-2">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-sky-600">
        Period A
      </span>
      <select
        value={a}
        onChange={(e) => onChange(e.target.value, b)}
        className="rounded border bg-background px-2 py-1 text-xs"
      >
        {PERIOD_PRESETS.map(([k, l]) => (
          <option key={k} value={k}>
            {l}
          </option>
        ))}
      </select>
    </div>
    <span className="text-xs text-muted-foreground">vs</span>
    <div className="flex items-center gap-2">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-amber-600">
        Period B
      </span>
      <select
        value={b}
        onChange={(e) => onChange(a, e.target.value)}
        className="rounded border bg-background px-2 py-1 text-xs"
      >
        {PERIOD_PRESETS.map(([k, l]) => (
          <option key={k} value={k}>
            {l}
          </option>
        ))}
      </select>
    </div>
    <button
      type="button"
      onClick={onClose}
      className="ml-auto inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
    >
      <X className="h-3.5 w-3.5" /> Exit compare
    </button>
  </div>
);

// ── Element completion funnel ───────────────────────────────────────────────
const ELEMENT_META = [
  ["Identity", "#2563eb", "Step 1 · required"],
  ["Cognition", "#7c3aed", "Step 2"],
  ["Resolution", "#0891b2", "Step 3"],
  ["Motivation", "#059669", "Step 4"],
];

export const ElementFunnel = ({ data, compare }) => {
  const n = (data?.n || data?.a?.n || 1) || 1;
  return (
    <div className="flex items-end gap-5 px-4 pt-4" style={{ height: 200 }}>
      {ELEMENT_META.map(([name, color, step]) => {
        if (compare) {
          const na = data.a?.n || 1;
          const nb = data.b?.n || 1;
          const pa = Math.round(((data.a?.[name] || 0) / na) * 100);
          const pb = Math.round(((data.b?.[name] || 0) / nb) * 100);
          return (
            <div key={name} className="flex flex-1 flex-col items-center justify-end gap-1.5" style={{ height: "100%" }}>
              <span className="text-sm font-semibold">
                <span className="text-sky-600">{pa}%</span>
                <span className="mx-1 text-muted-foreground">·</span>
                <span className="text-amber-600">{pb}%</span>
              </span>
              <div className="flex w-full max-w-[110px] items-end gap-1" style={{ height: 120 }}>
                <div className="flex-1 rounded-t bg-sky-500/80" style={{ height: `${pa}%`, minHeight: 3 }} />
                <div className="flex-1 rounded-t bg-amber-500/80" style={{ height: `${pb}%`, minHeight: 3 }} />
              </div>
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{name}</span>
              <DeltaBadge a={pa} b={pb} />
            </div>
          );
        }
        const count = data?.[name] || 0;
        const pct = Math.round((count / n) * 100);
        return (
          <div key={name} className="flex flex-1 flex-col items-center justify-end gap-1.5" style={{ height: "100%" }}>
            <span className="text-base font-semibold" style={{ color }}>
              {pct}%
            </span>
            <div className="flex w-full max-w-[110px] items-end" style={{ height: 120 }}>
              <div
                className="w-full rounded-t"
                style={{ height: `${pct}%`, minHeight: 3, background: color, opacity: 0.8 }}
                title={`${name}: ${count} of ${n} users · ${pct}%`}
              />
            </div>
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{name}</span>
            <span className="text-[9px] text-sky-600/70">{step}</span>
          </div>
        );
      })}
    </div>
  );
};

// ── Histogram (active hours / active days) ──────────────────────────────────
export const HistogramChart = ({ counts, labels, color = "#818cf8", height = 150 }) => {
  const data = (counts || []).map((c, i) => ({
    label: labels ? labels[i] : String(i),
    count: c,
  }));
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={labels ? 0 : 2} />
        <YAxis tick={{ fontSize: 10 }} allowDecimals={false} width={28} />
        <Tooltip />
        <Bar dataKey="count" fill={color} radius={[2, 2, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
};

export const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
export const HOUR_LABELS = Array.from({ length: 24 }, (_, h) => `${h}`);

// Reusable panel wrapper matching the dashboards' card style.
export const Panel = ({ title, right, children, className }) => (
  <Card className={cn(CARD_CLASS, className)}>
    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
      <CardTitle className="text-sm">{title}</CardTitle>
      {right || null}
    </CardHeader>
    <CardContent>{children}</CardContent>
  </Card>
);

// Bordered toolbar wrapper for the filter/control bar.
export const Toolbar = ({ children, className }) => (
  <div
    className={cn(
      "flex flex-wrap items-center gap-x-4 gap-y-2 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm",
      className
    )}
  >
    {children}
  </div>
);

// Bluish-gray page backdrop (#eef1f5, matching the mockups) that bleeds past
// the surrounding p-6 container so the white cards/toolbar pop. The negative
// margin cancels the container padding; inner padding restores spacing.
// fillViewport=false drops the tall min-height for nested use (e.g. inside the
// corporate-detail tab card, where the surface should size to its content).
export const DashboardSurface = ({ children, className, fillViewport = true }) => (
  <div
    className={cn(
      "-m-6 bg-[#eef1f5] p-6",
      fillViewport && "min-h-[calc(100vh-3rem)]",
      className
    )}
  >
    {children}
  </div>
);
